import styled from '@emotion/styled';
import { V1Service } from '@zelos/codegen/apis';
import { regex } from '@zelos/shared/constants/regex';
import { setReactHookFormErrorByErrors, toTypedApiError } from '@zelos/shared/lib/typed-api-error';
import { useSetAtom } from 'jotai';
import Image from 'next/image';
import { useForm } from 'react-hook-form';
import { tempFindUserAtom } from '../..';
import { InputAboveDiv, InputWrapDiv, InputWrapSpan } from '../../SignInput';
import SnsSign from '../../SnsSign';
import { useSignPageControl } from '../../use-sign-page-control';
import Button from '@/components/comm/Button';
import Input from '@/components/comm/Input';
import { breakpointMedium, breakpointSmall } from '@/constants/breakpoints';
import { colors } from '@/constants/colors';
import { useBreakpoint } from '@/lib/breakpoint.service';
import { createErrorToast } from '@/lib/toast';
import icMessage from '@/public/images/ic-message.svg';

type CheckEmailDuplicateType = { email: string };

const Container = styled.div`
padding: 0 32px;
text-align: center;

@media (max-width: ${breakpointSmall}) {
  padding: 0 20px;
}
`;

const ContentFirstDiv = styled.div`
margin-top: 40px;
color: ${colors.black};
font-size: 18px;
font-weight: 700;
line-height: 26px;
`;

const ContentSecondDiv = styled.div`
width: 90%;
margin: 16px auto 12px auto;
color: ${colors.indigo};
font-size: 14px;
font-weight: 400;
line-height: 24px;
word-break: keep-all;

@media (max-width: ${breakpointMedium}) {
  width: 100%;
}

@media (max-width: ${breakpointSmall}) {
  width: 80%;
}
`;

const ContentThirdDiv = styled.div`
width: 90%;
margin: 0 auto 55px auto;
color: ${colors.grey4};
font-size: 13px;
font-weight: 400;
line-height: 19px;
word-break: keep-all;
`;

const DirectSignUpGuideDiv = styled.div`
margin-top: 56px;
color: ${colors.grey4};
font-size: 13px;
font-weight: 400;
line-height: 19px;
`;

const DirectSignUpDiv = styled.div`
margin-top: 3px;
color: ${colors.grey3};
cursor: pointer;
font-size: 13px;
font-weight: 500;
line-height: 19px;
`;

const FormButton = styled(Button)`
width: 100%;
height: 54px;
margin-top: 74px;

@media (max-width: ${breakpointSmall}) {
  margin-top: 30px;
}
`;

const CheckEmailDuplicate = () => {
  const breakpoint = useBreakpoint();
  const setTempFindUser = useSetAtom(tempFindUserAtom);
  const { push } = useSignPageControl();
  const { register, handleSubmit, setError, formState: { errors } } = useForm<CheckEmailDuplicateType>();

  const checkEmailSubmit = async ({ email }: CheckEmailDuplicateType) => {
    try {
      const { isDuplicate } = await V1Service.v1UsersEmailDuplicateCreate({ requestBody: { email } });
      setTempFindUser({ email });
      if (!isDuplicate) push('notuser');
      else push('alreadyuser');
    } catch (error) {
      const errorBody = toTypedApiError(error);
      if (errorBody) {
        setReactHookFormErrorByErrors(setError, errorBody.errors);
        createErrorToast(errorBody, 'detail');
      } else {
        throw error;
      }
    }
  };

  return (
    <Container>
      <form noValidate onSubmit={handleSubmit(checkEmailSubmit)}>
        <ContentFirstDiv>통합회원 여부 확인</ContentFirstDiv>
        <ContentSecondDiv>프로그래머스 계정으로 사용 중인 이메일을 입력하시면 회원가입 여부를 정확하게 확인하실 수 있습니다.</ContentSecondDiv>
        <ContentThirdDiv>입력하신 정보는 회원가입 여부에만 사용되며 저장되지 않습니다.</ContentThirdDiv>
        <InputWrapDiv>
          <InputAboveDiv>
            <InputWrapSpan>이메일</InputWrapSpan>
          </InputAboveDiv>
          <Input
            type="email"
            placeholder={breakpoint === 'sm' ? '이메일을 입력해 주세요' : '프로그래머스 계정 이메일을 입력해 주세요'}
            error={errors.email}
            {...register('email', {
              required: '이메일을 입력해 주세요',
              pattern: {
                value: regex.email,
                message: '올바르지 않은 이메일 형식입니다.',
              },
            })}
            start={<Image src={icMessage} alt="message" />}
          />
        </InputWrapDiv>
        <FormButton type="submit">가입 여부 확인하기</FormButton>
        <DirectSignUpGuideDiv>바로 회원가입을 진행하시겠어요?</DirectSignUpGuideDiv>
        <DirectSignUpDiv onClick={() => push('up')}>회원가입하기 &gt;</DirectSignUpDiv>
      </form>
      <SnsSign text="다른 계정으로 가입하기" />
    </Container>
  );
};

export default CheckEmailDuplicate;
